import { type StateCreator } from "zustand";
import {
  THierarchyRelationship,
  THierarchyConfiguration,
  TConflict,
  THierarchyEmployee,
  TPreviewChanges,
} from "../../api/organization-hierarchy/organization-hierarchy.types";
import { TEmployee } from "../../api/employee-management/employee-management.types";
import { THierarchyFilters } from "../../core/types/organization-hierarchy";

export type TOrganizationHierarchyStoreState = {
  // Employee-based Relationships
  relationships: THierarchyRelationship[];
  selectedRelationship: THierarchyRelationship | null;
  relationshipsLoading: boolean;
  relationshipsError: string | null;

  // Configuration
  configuration: THierarchyConfiguration | null;
  configurationLoading: boolean;
  configurationError: string | null;

  // Conflicts
  conflicts: TConflict[];
  conflictsLoading: boolean;
  conflictsError: string | null;

  // Employees (replacing users)
  employees: THierarchyEmployee[];
  employeesLoading: boolean;
  employeesError: string | null;

  // Employee data cache for hierarchy operations
  employeeCache: Map<number, TEmployee>;
  employeeCacheLoading: boolean;
  employeeCacheError: string | null;

  // Employee search and filtering
  employeeSearchResults: THierarchyEmployee[];
  employeeSearchLoading: boolean;
  employeeSearchError: string | null;
  employeeSearchQuery: string;

  // Preview Changes
  previewChanges: TPreviewChanges | null;
  previewChangesLoading: boolean;
  previewChangesError: string | null;

  // Filters and Pagination
  filters: THierarchyFilters;
  pagination: {
    count: number;
    next: string | null;
    previous: string | null;
  };

  // UI State
  activeTab: string;
  selectedDate: string;
  isEditing: boolean;
  isAddingNew: boolean;

  // Employee-specific UI state
  selectedEmployees: number[];
  employeeHierarchyView: "tree" | "matrix" | "list";
  showInactiveEmployees: boolean;
};

export type TOrganizationHierarchyStoreActions = {
  // Employee-based Relationships
  setRelationships: (relationships: THierarchyRelationship[]) => void;
  addRelationship: (relationship: THierarchyRelationship) => void;
  updateRelationship: (relationship: THierarchyRelationship) => void;
  removeRelationship: (id: string) => void;
  setSelectedRelationship: (
    relationship: THierarchyRelationship | null,
  ) => void;
  setRelationshipsLoading: (loading: boolean) => void;
  setRelationshipsError: (error: string | null) => void;

  // Configuration
  setConfiguration: (configuration: THierarchyConfiguration | null) => void;
  setConfigurationLoading: (loading: boolean) => void;
  setConfigurationError: (error: string | null) => void;

  // Conflicts
  setConflicts: (conflicts: TConflict[]) => void;
  addConflict: (conflict: TConflict) => void;
  removeConflict: (id: string) => void;
  setConflictsLoading: (loading: boolean) => void;
  setConflictsError: (error: string | null) => void;

  // Employees (replacing users)
  setEmployees: (employees: THierarchyEmployee[]) => void;
  addEmployee: (employee: THierarchyEmployee) => void;
  updateEmployee: (employee: THierarchyEmployee) => void;
  removeEmployee: (id: number) => void;
  setEmployeesLoading: (loading: boolean) => void;
  setEmployeesError: (error: string | null) => void;

  // Employee data cache management
  setEmployeeCache: (cache: Map<number, TEmployee>) => void;
  updateEmployeeCache: (employeeId: number, employee: TEmployee) => void;
  removeFromEmployeeCache: (employeeId: number) => void;
  setEmployeeCacheLoading: (loading: boolean) => void;
  setEmployeeCacheError: (error: string | null) => void;

  // Employee search and filtering
  setEmployeeSearchResults: (results: THierarchyEmployee[]) => void;
  setEmployeeSearchLoading: (loading: boolean) => void;
  setEmployeeSearchError: (error: string | null) => void;
  setEmployeeSearchQuery: (query: string) => void;
  clearEmployeeSearch: () => void;

  // Preview Changes
  setPreviewChanges: (changes: TPreviewChanges | null) => void;
  setPreviewChangesLoading: (loading: boolean) => void;
  setPreviewChangesError: (error: string | null) => void;

  // Filters and Pagination
  setFilters: (filters: Partial<THierarchyFilters>) => void;
  resetFilters: () => void;
  setPagination: (pagination: {
    count: number;
    next: string | null;
    previous: string | null;
  }) => void;

  // UI State
  setActiveTab: (tab: string) => void;
  setSelectedDate: (date: string) => void;
  setIsEditing: (editing: boolean) => void;
  setIsAddingNew: (adding: boolean) => void;

  // Employee-specific UI state
  setSelectedEmployees: (employeeIds: number[]) => void;
  addSelectedEmployee: (employeeId: number) => void;
  removeSelectedEmployee: (employeeId: number) => void;
  clearSelectedEmployees: () => void;
  setEmployeeHierarchyView: (view: "tree" | "matrix" | "list") => void;
  setShowInactiveEmployees: (show: boolean) => void;

  // Employee hierarchy operations
  getEmployeeHierarchyPath: (
    fromEmployeeId: number,
    toEmployeeId: number,
  ) => THierarchyRelationship[];
  getEmployeeSubordinates: (employeeId: number) => THierarchyEmployee[];
  getEmployeeSupervisors: (employeeId: number) => THierarchyEmployee[];
  isEmployeeAccessible: (supervisorId: number, employeeId: number) => boolean;

  // Reset
  resetOrganizationHierarchyStore: () => void;
};

const initialOrganizationHierarchyStoreState: TOrganizationHierarchyStoreState =
  {
    // Employee-based Relationships
    relationships: [],
    selectedRelationship: null,
    relationshipsLoading: false,
    relationshipsError: null,

    // Configuration
    configuration: null,
    configurationLoading: false,
    configurationError: null,

    // Conflicts
    conflicts: [],
    conflictsLoading: false,
    conflictsError: null,

    // Employees (replacing users)
    employees: [],
    employeesLoading: false,
    employeesError: null,

    // Employee data cache
    employeeCache: new Map<number, TEmployee>(),
    employeeCacheLoading: false,
    employeeCacheError: null,

    // Employee search and filtering
    employeeSearchResults: [],
    employeeSearchLoading: false,
    employeeSearchError: null,
    employeeSearchQuery: "",

    // Preview Changes
    previewChanges: null,
    previewChangesLoading: false,
    previewChangesError: null,

    // Filters and Pagination
    filters: {
      page: 1,
      page_size: 20,
    },
    pagination: {
      count: 0,
      next: null,
      previous: null,
    },

    // UI State
    activeTab: "matrix-builder",
    selectedDate: new Date().toISOString().split("T")[0],
    isEditing: false,
    isAddingNew: false,

    // Employee-specific UI state
    selectedEmployees: [],
    employeeHierarchyView: "tree",
    showInactiveEmployees: false,
  };

export type TOrganizationHierarchyStore = TOrganizationHierarchyStoreActions &
  TOrganizationHierarchyStoreState;

export const createOrganizationHierarchySlice: StateCreator<
  TOrganizationHierarchyStore
> = (set, get) => ({
  ...initialOrganizationHierarchyStoreState,

  // Employee-based Relationships
  setRelationships: (relationships) => set({ relationships }),
  addRelationship: (relationship) =>
    set((state) => ({
      relationships: [...state.relationships, relationship],
    })),
  updateRelationship: (relationship) =>
    set((state) => ({
      relationships: state.relationships.map((r) =>
        r.id === relationship.id ? relationship : r,
      ),
    })),
  removeRelationship: (id: string | number) =>
    set((state) => ({
      relationships: state.relationships.filter((r) => r.id !== id),
    })),
  setSelectedRelationship: (relationship) =>
    set({ selectedRelationship: relationship }),
  setRelationshipsLoading: (loading) => set({ relationshipsLoading: loading }),
  setRelationshipsError: (error) => set({ relationshipsError: error }),

  // Configuration
  setConfiguration: (configuration) => set({ configuration }),
  setConfigurationLoading: (loading) => set({ configurationLoading: loading }),
  setConfigurationError: (error) => set({ configurationError: error }),

  // Conflicts
  setConflicts: (conflicts) => set({ conflicts }),
  addConflict: (conflict) =>
    set((state) => ({
      conflicts: [...state.conflicts, conflict],
    })),
  removeConflict: (id) =>
    set((state) => ({
      conflicts: state.conflicts.filter((c) => c.id !== id),
    })),
  setConflictsLoading: (loading) => set({ conflictsLoading: loading }),
  setConflictsError: (error) => set({ conflictsError: error }),

  // Employees (replacing users)
  setEmployees: (employees) => set({ employees }),
  addEmployee: (employee) =>
    set((state) => ({
      employees: [...state.employees, employee],
    })),
  updateEmployee: (employee) =>
    set((state) => ({
      employees: state.employees.map((e) =>
        e.id === employee.id ? employee : e,
      ),
    })),
  removeEmployee: (id) =>
    set((state) => ({
      employees: state.employees.filter((e) => e.id !== id),
    })),
  setEmployeesLoading: (loading) => set({ employeesLoading: loading }),
  setEmployeesError: (error) => set({ employeesError: error }),

  // Employee data cache management
  setEmployeeCache: (cache) => set({ employeeCache: cache }),
  updateEmployeeCache: (employeeId, employee) =>
    set((state) => {
      const newCache = new Map(state.employeeCache);
      newCache.set(employeeId, employee);
      return { employeeCache: newCache };
    }),
  removeFromEmployeeCache: (employeeId) =>
    set((state) => {
      const newCache = new Map(state.employeeCache);
      newCache.delete(employeeId);
      return { employeeCache: newCache };
    }),
  setEmployeeCacheLoading: (loading) => set({ employeeCacheLoading: loading }),
  setEmployeeCacheError: (error) => set({ employeeCacheError: error }),

  // Employee search and filtering
  setEmployeeSearchResults: (results) =>
    set({ employeeSearchResults: results }),
  setEmployeeSearchLoading: (loading) =>
    set({ employeeSearchLoading: loading }),
  setEmployeeSearchError: (error) => set({ employeeSearchError: error }),
  setEmployeeSearchQuery: (query) => set({ employeeSearchQuery: query }),
  clearEmployeeSearch: () =>
    set({
      employeeSearchResults: [],
      employeeSearchQuery: "",
      employeeSearchError: null,
    }),

  // Preview Changes
  setPreviewChanges: (changes) => set({ previewChanges: changes }),
  setPreviewChangesLoading: (loading) =>
    set({ previewChangesLoading: loading }),
  setPreviewChangesError: (error) => set({ previewChangesError: error }),

  // Filters and Pagination
  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
    })),
  resetFilters: () =>
    set({ filters: initialOrganizationHierarchyStoreState.filters }),
  setPagination: (pagination) => set({ pagination }),

  // UI State
  setActiveTab: (tab) => set({ activeTab: tab }),
  setSelectedDate: (date) => set({ selectedDate: date }),
  setIsEditing: (editing) => set({ isEditing: editing }),
  setIsAddingNew: (adding) => set({ isAddingNew: adding }),

  // Employee-specific UI state
  setSelectedEmployees: (employeeIds) =>
    set({ selectedEmployees: employeeIds }),
  addSelectedEmployee: (employeeId) =>
    set((state) => ({
      selectedEmployees: [...state.selectedEmployees, employeeId],
    })),
  removeSelectedEmployee: (employeeId) =>
    set((state) => ({
      selectedEmployees: state.selectedEmployees.filter(
        (id) => id !== employeeId,
      ),
    })),
  clearSelectedEmployees: () => set({ selectedEmployees: [] }),
  setEmployeeHierarchyView: (view) => set({ employeeHierarchyView: view }),
  setShowInactiveEmployees: (show) => set({ showInactiveEmployees: show }),

  // Employee hierarchy operations
  getEmployeeHierarchyPath: (fromEmployeeId, toEmployeeId) => {
    const state = get();
    const path: THierarchyRelationship[] = [];

    // Simple path finding - in a real implementation, this would use a graph traversal algorithm
    const findPath = (
      currentId: number,
      targetId: number,
      visited: Set<number>,
    ): boolean => {
      if (currentId === targetId) return true;
      if (visited.has(currentId)) return false;

      visited.add(currentId);

      // Find relationships where current employee is the parent
      const childRelationships = state.relationships.filter(
        (r) =>
          r.parent_employee_id === currentId &&
          r.effective_end_date === undefined,
      );

      for (const relationship of childRelationships) {
        if (
          typeof relationship.child_employee_id === "number" &&
          findPath(relationship.child_employee_id, targetId, visited)
        ) {
          path.unshift(relationship);
          return true;
        }
      }

      return false;
    };

    findPath(fromEmployeeId, toEmployeeId, new Set());
    return path;
  },

  getEmployeeSubordinates: (employeeId) => {
    const state = get();
    const subordinateIds = state.relationships
      .filter(
        (r) =>
          r.parent_employee_id === employeeId &&
          r.effective_end_date === undefined,
      )
      .map((r) => r.child_employee_id);

    return state.employees.filter((e) => subordinateIds.includes(e.id));
  },

  getEmployeeSupervisors: (employeeId) => {
    const state = get();
    const supervisorIds = state.relationships
      .filter(
        (r) =>
          r.child_employee_id === employeeId &&
          r.effective_end_date === undefined,
      )
      .map((r) => r.parent_employee_id);

    return state.employees.filter((e) => supervisorIds.includes(e.id));
  },

  isEmployeeAccessible: (supervisorId, employeeId) => {
    const state = get();

    // Check direct relationship
    const directRelationship = state.relationships.find(
      (r) =>
        r.parent_employee_id === supervisorId &&
        r.child_employee_id === employeeId &&
        r.effective_end_date === undefined,
    );

    if (directRelationship) return true;

    // Check indirect relationship through hierarchy path
    const path = get().getEmployeeHierarchyPath(supervisorId, employeeId);
    return path.length > 0;
  },

  // Reset
  resetOrganizationHierarchyStore: () =>
    set(initialOrganizationHierarchyStoreState),
});
